import { useState } from 'react'
import { Zap } from 'lucide-react'
import { decodeCalldata, short } from '../decode.js'
import { usePrivacy } from '../lib/privacy.js'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DecodedCalldata } from './DecodedCalldata.js'
import { ClearSigningPanel } from './ClearSigningPanel.js'
import { resolveClearSigning } from '../erc7730/resolve.js'
import type { Request } from '../hooks/useWalletKit.js'

interface Props {
  request: Request
  signerConnected: boolean
  onApprove: () => Promise<void>
  onReject: () => Promise<void>
}

interface CallView {
  to: string
  value: string
  data: string
  summary: string
}

export function RequestCard({
  request,
  signerConnected,
  onApprove,
  onReject,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'approved' | 'rejected' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  usePrivacy()

  const method = request.params.request.method
  const calls = extractCalls(request)
  const messagePreview = extractMessagePreview(request)
  const supported =
    method === 'eth_sendTransaction' ||
    method === 'wallet_sendCalls' ||
    method === 'personal_sign' ||
    method === 'eth_signTypedData_v4'
  const isSignMessage =
    method === 'personal_sign' || method === 'eth_signTypedData_v4'

  const approve = async () => {
    setBusy(true)
    setErr(null)
    try {
      await onApprove()
      setDone('approved')
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    setBusy(true)
    try {
      await onReject()
      setDone('rejected')
    } finally {
      setBusy(false)
    }
  }

  if (done === 'approved') {
    return (
      <Card className="mb-4 border-l-4 border-l-emerald-500">
        <CardContent>
          <p className="text-sm text-emerald-400">
            ✓{' '}
            {isSignMessage
              ? 'Signed off-chain — Guardian co-signed and the dApp received the signature.'
              : 'Proposed to Safe Tx Service. Guardian co-sign in progress, refreshing the pending list…'}
          </p>
        </CardContent>
      </Card>
    )
  }
  if (done === 'rejected') return null

  const isBatch = method === 'wallet_sendCalls'

  return (
    <Card className="mb-4 border-l-4 border-l-sky-500">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Zap className="size-4 text-sky-400" />
          {isBatch
            ? `Batch sign request (${calls.length} calls)`
            : 'Sign request'}
        </CardTitle>
        <Badge className="border-sky-500/30 bg-sky-500/10 text-sky-400">
          {method}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        {!supported && (
          <div className="text-sm text-destructive">
            v1 doesn't handle {method}. Click Reject — the dApp will surface a
            polite error.
          </div>
        )}

        {!signerConnected && supported && (
          <div className="text-sm text-destructive">
            ⚠ No signer wallet connected. Pair Rabby in the “Signer wallet” card
            above before approving — we need it to sign as Ainura.
          </div>
        )}

        {calls.map((c, i) => (
          <details
            key={i}
            open={calls.length <= 3}
            className="rounded-md border bg-muted/20 px-3 py-2"
          >
            <summary className="cursor-pointer list-none font-mono text-sm">
              {calls.length > 1 && (
                <span className="text-muted-foreground">
                  Call {i + 1}:{' '}
                </span>
              )}
              {short(c.to)}{' '}
              <span className="text-muted-foreground" data-mask>
                {c.summary}
              </span>
            </summary>
            <dl className="mt-3 grid grid-cols-[100px_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Target</dt>
              <dd className="font-mono">{short(c.to)}</dd>
              <dt className="text-muted-foreground">Value</dt>
              <dd className="font-mono" data-mask>
                {c.value} wei
              </dd>
              <dt className="text-muted-foreground">Calldata</dt>
              <dd className="tx-data max-h-20 overflow-auto rounded border bg-background p-2 font-mono text-[11px] break-all">
                {c.data}
              </dd>
            </dl>
            <ClearSigningPanel to={c.to} data={c.data} value={c.value} />
            <DecodedCalldata
              data={c.data}
              collapsed={resolveClearSigning(c.to, c.data, { value: c.value }) !== null}
            />
          </details>
        ))}

        {messagePreview && (
          <details
            open
            className="rounded-md border bg-muted/20 px-3 py-2"
          >
            <summary className="cursor-pointer text-sm text-muted-foreground">
              {messagePreview.summary}
            </summary>
            <pre className="tx-data mt-2 max-h-60 overflow-auto rounded border bg-background p-2 font-mono text-[11px] whitespace-pre-wrap break-all">
              {messagePreview.body}
            </pre>
          </details>
        )}

        <p className="text-xs text-muted-foreground">
          {isSignMessage
            ? `Approving asks Rabby to sign a SafeMessage typed-data over the Module Safe domain. The Guardian co-signs server-side after running policy (permits to non-whitelisted spenders are refused). Both ECDSA sigs are wrapped as a nested EIP-1271 contract signature pointing at the Module Safe and returned to the dApp. Pure off-chain — no gas, no on-chain tx. Only works for dApps that verify via Safe.isValidSignature.`
            : isBatch
              ? `Approving wraps these as a single MultiSend call (DELEGATECALL via Main Safe), asks Rabby for Ainura's sig, and proposes one atomic Module Safe tx. Guardian co-signs automatically if policy passes.`
              : `Approving wraps this as \`execTransactionFromModule\` on the Main Safe, asks Rabby for Ainura's sig, and proposes to Safe Tx Service. Guardian co-signs automatically if policy passes.`}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => void approve()}
            disabled={busy || !supported || !signerConnected}
            title={
              !signerConnected
                ? 'Pair Rabby first'
                : !supported
                  ? `${method} isn't handled in v1`
                  : undefined
            }
          >
            {busy ? 'Working…' : 'Approve & sign'}
          </Button>
          <Button variant="outline" onClick={() => void reject()} disabled={busy}>
            Reject
          </Button>
          {err && <span className="text-sm text-destructive">{err}</span>}
        </div>
      </CardContent>
    </Card>
  )
}

function extractCalls(req: Request): CallView[] {
  const method = req.params.request.method
  if (method === 'eth_sendTransaction') {
    const tx = (req.params.request.params?.[0] ?? {}) as {
      to?: string
      value?: string
      data?: string
    }
    if (!tx.to) return []
    return [callView(tx.to, tx.value ?? '0', (tx.data ?? '0x') as `0x${string}`)]
  }
  if (method === 'wallet_sendCalls') {
    const arg = (req.params.request.params?.[0] ?? {}) as {
      calls?: Array<{ to: string; value?: string; data?: `0x${string}` }>
    }
    return (arg.calls ?? []).map((c) =>
      callView(c.to, c.value ?? '0x0', (c.data ?? '0x') as `0x${string}`),
    )
  }
  return []
}

function callView(to: string, value: string, data: `0x${string}`): CallView {
  const valueDec = value.startsWith('0x') ? BigInt(value).toString() : value
  return {
    to,
    value: valueDec,
    data,
    summary: decodeCalldata(to, valueDec, data).summary,
  }
}

interface MessagePreview {
  summary: string
  body: string
}

function extractMessagePreview(req: Request): MessagePreview | null {
  const method = req.params.request.method
  const params = req.params.request.params
  if (method === 'personal_sign') {
    const a = params?.[0]
    const b = params?.[1]
    const looksLikeAddress = (v: unknown) =>
      typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)
    const message =
      typeof a === 'string' && !looksLikeAddress(a)
        ? a
        : typeof b === 'string'
          ? b
          : ''
    let body = message
    if (message.startsWith('0x')) {
      try {
        body = decodeURIComponent(
          Array.from(message.slice(2).match(/.{1,2}/g) ?? [])
            .map((h) => '%' + h)
            .join(''),
        )
      } catch {
        body = message
      }
    }
    return { summary: 'personal_sign message', body }
  }
  if (method === 'eth_signTypedData_v4') {
    const raw = params?.[1]
    let payload: unknown
    try {
      payload = typeof raw === 'string' ? JSON.parse(raw) : raw
    } catch {
      return { summary: 'eth_signTypedData_v4 (unparseable)', body: String(raw) }
    }
    if (!payload || typeof payload !== 'object') return null
    const p = payload as {
      domain?: { name?: string; verifyingContract?: string }
      primaryType?: string
      message?: unknown
    }
    const summary = `${p.primaryType ?? 'TypedData'} on ${p.domain?.name ?? '?'}${
      p.domain?.verifyingContract
        ? ' @ ' + p.domain.verifyingContract.slice(0, 10) + '…'
        : ''
    }`
    return { summary, body: JSON.stringify(p.message, null, 2) }
  }
  return null
}
